import { Button, Card, Flex, Form, type FormProps, Select } from "antd"
import type { ValidateErrorEntity } from "rc-field-form/es/interface"
import React, { useCallback, useRef, useState } from "react"

import { FormChangedWatcher } from "../../../../components/Forms/FormChangedWatcher/FormChangedWatcher.tsx"
import { useDateFormat } from "../../../../hooks/use-date-format.ts"
import { useNotifications } from "../../../../hooks/use-notifications.ts"
import { TEMPLATE_DOCUMENT_TYPES_LIST } from "../../constants/template-document-type.tsx"
import type {
  DocumentAvailableBlock,
  DocumentTemplateBlock,
  DocumentTemplateMutateData
} from "../../types/types.ts"
import {
  DocumentTemplateFormBody,
  type DocumentTemplateFormBodyHandle
} from "./DocumentTemplateFormBody.tsx"
import { DocumentTemplateFormGroups } from "./DocumentTemplateFormGroups.tsx"
import { DocumentTemplateFormPreview } from "./DocumentTemplateFormPreview.tsx"
import { TextInput } from "../../../../components/Input/TextInput.tsx"

interface ProposalTemplatesBuilderFormProps {
  availableBlocks: DocumentAvailableBlock[]
  initialValues?: Partial<DocumentTemplateMutateData>
  onSubmit: (payload: DocumentTemplateMutateData) => Promise<void>
  onPreview: (payload: DocumentTemplateMutateData) => Promise<string> // html
}

export function DocumentTemplateForm({
  availableBlocks,
  initialValues,
  onSubmit,
  onPreview
}: ProposalTemplatesBuilderFormProps) {
  const { normalizeDate } = useDateFormat()
  const [loading, setLoading] = useState(false)
  const [form] = Form.useForm<DocumentTemplateMutateData>()

  const formBodyRef = useRef<DocumentTemplateFormBodyHandle>(null)
  const notification = useNotifications()

  const handleAddBlock = useCallback(
    (block: DocumentAvailableBlock) => {
      const currentBlocks: DocumentTemplateBlock[] =
        form.getFieldValue("blocks") || []

      if (block.max_per_template !== null) {
        const count = currentBlocks.filter(
          (b) => b.block_type === block.block_type
        ).length

        if (count >= block.max_per_template) {
          notification.warning({
            message: `Maximum ${block.max_per_template} "${block.label}" block(s) per template`
          })
          return
        }
      }

      const newBlock: DocumentTemplateBlock = {
        id: crypto.randomUUID(),
        block_type: block.block_type,
        order: currentBlocks.length,
        config: { ...block.default_config }
      }

      form.setFieldValue("blocks", [...currentBlocks, newBlock])
    },
    [form, notification]
  )

  const handleClearAll = useCallback(() => {
    const requiredBlocks = availableBlocks?.filter((b) => !b?.removable)

    if (Array.isArray(requiredBlocks)) {
      const initialBlocks = requiredBlocks?.map((b, i) => ({
        id: crypto.randomUUID(),
        block_type: b?.block_type,
        order: i,
        config: b?.default_config
      }))

      form.setFieldValue("blocks", initialBlocks)
    } else {
      form.setFieldValue("blocks", [])
    }
  }, [availableBlocks, form])

  const onFinishHandler = useCallback(
    async ({
      completion_date,
      start_date,
      ...values
    }: DocumentTemplateMutateData) => {
      try {
        setLoading(true)
        await onSubmit({
          ...values,
          completion_date: normalizeDate(completion_date),
          start_date: normalizeDate(start_date)
        })
      } finally {
        setLoading(false)
      }
    },
    [onSubmit]
  )

  const handleStepValidationError = useCallback(
    (errorFields: { name: (string | number)[] }[]) => {
      const headerFields = ["name", "doc_type"]

      const hasHeaderError = errorFields.some((f) =>
        headerFields.includes(f.name[0] as string)
      )

      if (hasHeaderError) return

      formBodyRef.current?.goToStep(1)
      setTimeout(() => void form.validateFields(), 400)
    },
    [form, formBodyRef]
  )

  const onFinishFailed = useCallback(
    ({
      errorFields
    }: Parameters<NonNullable<FormProps["onFinishFailed"]>>[0]) => {
      handleStepValidationError(errorFields)
    },
    [handleStepValidationError]
  )

  const onValidate =
    useCallback(async (): Promise<DocumentTemplateMutateData | null> => {
      try {
        return await form.validateFields()
      } catch (e: any) {
        const errorFields = (e as ValidateErrorEntity).errorFields || []
        handleStepValidationError(errorFields)

        if (errorFields?.length) {
          form.scrollToField(errorFields[0].name, {
            behavior: "smooth",
            block: "center",
            scrollMode: "always"
          })
        }

        return null
      }
    }, [form, handleStepValidationError])

  return (
    <Form<DocumentTemplateMutateData>
      className={
        "flex flex-col gap-4 flex-1 overflow-hidden with-default-error"
      }
      onFinish={onFinishHandler}
      onFinishFailed={onFinishFailed}
      validateTrigger={["onSubmit", "onChange"]}
      layout="vertical"
      form={form}
      scrollToFirstError={{
        behavior: "smooth",
        block: "center",
        scrollMode: "always"
      }}
      initialValues={initialValues}
      disabled={loading}
    >
      <FormChangedWatcher<DocumentTemplateMutateData>
        form={form}
        saving={loading}
      />

      <Card size={"small"}>
        <Flex gap={"small"} justify={"space-between"}>
          <Form.Item<DocumentTemplateMutateData>
            name="name"
            className={"mb-0 w-full"}
            rules={[
              {
                required: true,
                whitespace: true,
                message: "Please enter name"
              },
              { max: 255, message: "Name must be less than 255 characters" }
            ]}
          >
            <TextInput
              disabled={loading}
              maxLength={255}
              placeholder="Enter template name"
            />
          </Form.Item>

          <Flex align="center" gap="small">
            <Form.Item<DocumentTemplateMutateData>
              name={"doc_type"}
              className={"mb-0"}
              rules={[{ required: false, message: "Please select doc type" }]}
            >
              <Select
                disabled={loading}
                placeholder="Select type"
                className={"w-44"}
                options={TEMPLATE_DOCUMENT_TYPES_LIST}
              />
            </Form.Item>

            <Button disabled={loading} onClick={handleClearAll}>
              Clear All
            </Button>

            <DocumentTemplateFormPreview
              loading={loading}
              onValidate={onValidate}
              onPreview={onPreview}
              submit={form.submit}
            />

            <Button loading={loading} type="primary" htmlType={"submit"}>
              Save Template
            </Button>
          </Flex>
        </Flex>
      </Card>

      <Flex gap={"middle"} vertical className={"overflow-hidden "} flex={1}>
        <Flex className={"overflow-hidden"} flex={1} gap={"small"}>
          <Card size={"small"} className={"flex"} classNames={{ body: "flex" }}>
            <DocumentTemplateFormGroups
              loading={loading}
              availableBlocks={availableBlocks}
              onAdd={handleAddBlock}
            />
          </Card>
          <Card
            size={"small"}
            classNames={{ body: "w-full flex" }}
            className={"w-full flex overflow-hidden"}
          >
            <DocumentTemplateFormBody
              ref={formBodyRef}
              form={form}
              availableBlocks={availableBlocks}
              loading={loading}
            />
          </Card>
        </Flex>
      </Flex>
    </Form>
  )
}
